import React from 'react'
import { Link, matchPath, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import MuiLink from '@mui/material/Link'
import Container from '@mui/material/Container'

import Alert from '../components/alert/alert'
import {
  loadUser,
  userLoaded,
  setCurrentCustomer,
  userLoadError,
  UserLoadErrorSubtype,
  setCurrentMachine,
  currentMachineLoadError,
  setCuppingRoastIds,
} from '../store/actions'
import { useContextHook } from '../store/context'
import { Machine } from '../types/machine'
import { PagedResponse } from '../types/pagedResponse'
import { User } from '../types/user'
import { get, isAnonymousUrl, isLoggedIn, logoutUser } from './service'
import { getUsername, getSelectedCustomer, getSelectedCuppingRoasts, getLastSelectedMachine } from './localStorageUtils'

const Dataloader = (): JSX.Element => {
  const { t } = useTranslation()
  const { dispatch, state } = useContextHook()
  const location = useLocation()

  const machineMatch = matchPath('/machine/:machineId/*', location.pathname)

  React.useEffect(() => {
    // load current machine (if any)
    const loadSlug = machineMatch?.params.machineId || getLastSelectedMachine()
    if (isLoggedIn() && loadSlug && loadSlug !== state.currentMachine?.slug) {
      get<PagedResponse<Machine>>(`machines/?slug=${loadSlug}`)
        .then((response) => {
          if (response.data.results.length > 0) {
            dispatch(setCurrentMachine(response.data.results[0]))
          } else {
            dispatch(currentMachineLoadError()) // user does not have access to current machine slug!
          }
        })
        .catch(() => dispatch(currentMachineLoadError()))
    }
  }, [dispatch, state.currentMachine?.slug, machineMatch?.params.machineId])

  React.useEffect(() => {
    const username = getUsername()
    // check if we can find username in localStorage. If not, we are mot logged in!
    if (isLoggedIn() && username) {
      dispatch(loadUser())
      get<PagedResponse<User>>(`users/?username=${username}`)
        .then((response) => {
          if (response.data.results.length === 0) {
            // if we cannot load any users, display error
            dispatch(userLoadError(UserLoadErrorSubtype.NoUser))
          } else {
            dispatch(userLoaded(response.data.results[0]))

            const customers = response.data.results[0].customers

            if (customers.length === 0) {
              dispatch(userLoadError(UserLoadErrorSubtype.NoCustomer))
            } else {
              const customerUrlFromLocalStorage = getSelectedCustomer()
              const selectedCustomer = customers.find((x) => x.url === customerUrlFromLocalStorage)
              if (selectedCustomer) {
                const resetMachine = false
                dispatch(setCurrentCustomer(selectedCustomer, resetMachine))
              } else {
                const resetMachine = true
                dispatch(setCurrentCustomer(customers[0], resetMachine))
              }
            }
            dispatch(setCuppingRoastIds(getSelectedCuppingRoasts()))
          }
        })
        .catch(() => dispatch(userLoadError(UserLoadErrorSubtype.Unknown)))
    } else if (!isAnonymousUrl(window.location.pathname)) {
      logoutUser()
    }
  }, [dispatch])

  const getUserLoadErrorMessage = (error: UserLoadErrorSubtype) => {
    switch (error) {
      case UserLoadErrorSubtype.NoCustomer:
        return t('common.missingcustomererror')
      case UserLoadErrorSubtype.NoUser:
        return t('common.missingusererror')
      case UserLoadErrorSubtype.Unknown:
      default:
        return t('common.userloaderror')
    }
  }

  return state.loadUserError || state.currentMachineLoadError ? (
    <Container maxWidth="lg">
      {state.currentMachineLoadError && <Alert severity="error">{t('common.currentmachineerror')}</Alert>}
      {state.loadUserError && (
        <>
          <Alert severity="error">{getUserLoadErrorMessage(state.loadUserError)}</Alert>
          <MuiLink component={Link} to="/logout">
            {t('common.tologinpage')}
          </MuiLink>
        </>
      )}
    </Container>
  ) : (
    <></>
  )
}

export default Dataloader
